import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import s from "./AdminApplicationsList.module.scss";
import { Input, Table, Tabs, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CreditCardOutlined,
  EditOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  getAdminApplicationDetails,
  getAdminApplicationsList,
  getBrokersApplicationDetails,
  getMessagesList,
  searchApplications,
  setCurrentPageNumber,
  setLastSearchValue,
} from "../../redux/ApplicationSlice";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import Footer from "../../components/Footer/Footer";
import MobileHeader from "../../components/MobileHeader/MobileHeader";
import useWindowDimensions from "../../components/WindowDimensions/WindowDimensions";
import { getListStatusTag } from "../../utils/functions";
import MobileApplicationsList from "../MobileApplicationsList/MobileApplicationsList";
import { ICreateApplicationResponse } from "../../types";
import { getExchangeRate } from "../../redux/BillingSlice";
import { useTranslation } from "react-i18next";
import type { GetProps } from "antd";
interface TableType {
  number: number;
  status: any;
  application_number: string;
  registration_date: string;
  user?: string;
  children?: TableType[];
}
type Props = {};
const { Search } = Input;
type SearchProps = GetProps<typeof Input.Search>;

const AdminApplicationsList = (props: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const { width } = useWindowDimensions();

  const applicationsList = useSelector(
    (state: RootState) => state.application.applicationsList,
  );

  const user = useSelector((state: RootState) => state.user.user);

  const [data, setData] = useState<ICreateApplicationResponse[] | undefined>(
    applicationsList?.results,
  );
  const currentStatePage = useSelector(
    (state: RootState) => state.application.currentPage,
  );

  const [currentPage, setCurrentPage] = useState<number>(currentStatePage || 1);

  const lang = useSelector((state: RootState) => state.application.lang);
  const { t } = useTranslation();

  const [pagination, setPagination] = useState({
    current: applicationsList?.pagination?.current_page || 1,
    pageSize: applicationsList?.pagination?.page_size || 30,
    total: applicationsList?.pagination?.total_items || 100,
  });

  const currentPageRef = useRef(currentPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const fetchData = async ({ page }: { page: number }) => {
      let res;

      res = await dispatch(getAdminApplicationsList(page));
      if (
        res.payload &&
        res.type === "applications/getAdminApplicationsList/fulfilled" &&
        "results" in res.payload
      ) {
        setData(res.payload.results);
        setPagination({
          current: res.payload.pagination?.current_page,
          pageSize: res.payload.pagination?.page_size,
          total: res.payload.pagination?.total_items,
        });
      }
    };

    fetchData({
      page: currentPageRef.current,
    });

    const interval = setInterval(() => {
      const page = currentPageRef.current;

      fetchData({ page });
    }, 100000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const getNextPage = async (page: number) => {
    setCurrentPage(page);
    dispatch(setCurrentPageNumber(page));
    currentPageRef.current = page;

    if (page === undefined || page === null) return;

    try {
      const res = await dispatch(getAdminApplicationsList(page));
      if (!res.payload || !("results" in res.payload)) {
        console.error("Unexpected API response:", res);
        return;
      }

      if (res.type === "applications/getAdminApplicationsList/fulfilled") {
        if ("results" in res.payload) {
          setData(res.payload.results);
          setPagination({
            current: res?.payload?.pagination?.current_page,
            pageSize: res?.payload?.pagination.page_size,
            total: res.payload.pagination.total_items,
          });
        } else {
          console.error("Unexpected API response:", res.payload);
        }
      }
    } catch (error) {
      console.error("Error in getNextPage:", error);
    }
  };

  const columns: ColumnsType<TableType> = [
    {
      title: "№",
      dataIndex: "number",
      width: 20,
      align: "center",
    },
    {
      title: t("Статус"),
      dataIndex: `status`,
      width: 300,
    },
    {
      title: t("№ заявки"),
      dataIndex: `application_number`,
      width: 120,
      align: "center",
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("№ транспортного средства"),
      dataIndex: `vehicle_number`,
      width: 200,
      align: "center",
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Таможенный брокер"),
      dataIndex: `broker`,
      width: 200,
      align: "center",
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Региональный менеджер"),
      dataIndex: `manager`,
      width: 200,
      align: "center",
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Дата регистрации"),
      dataIndex: `registration_date`,
      width: 150,
      align: "center",
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Заявление"),
      dataIndex: `application`,
      width: 50,
    },
  ];

  const getPaymentAmount = async (id: number) => {
    const res = await dispatch(getBrokersApplicationDetails(id));
    if (res.type === "applications/getApplicationDetails/fulfilled") {
      navigate(`/payment/${id}`);
    }
  };

  const getMessages = async (id: number) => {
    const res = await dispatch(getMessagesList(Number(id)));

    if (res.type === "messages/getMessagesList/fulfilled") {
      navigate(`/messages/${id}`);
    }
  };
  const onSearch: SearchProps["onSearch"] = async (value, _e, info) => {
    const res = await dispatch(
      searchApplications({ search_term: value, page: 1 }),
    );

    dispatch(setLastSearchValue(value));
    console.log("res", res);
    if (res.type === "applications/searchApplications/fulfilled") {
      navigate("/search-results");
    }
  };

  const [prevPage, setPrevPage] = useState<number>(1);
  const [prevEmployeeId, setPrevEmployeeId] = useState<number | undefined>(
    undefined,
  );

  return (
    <div>
      {width > 985 ? <Header /> : <MobileHeader />}
      <div className={s.header}>
        <div className={s.top_header}>
          <div className={s.header_left}>
            {/* <div className={s.search_block}>
              {(user?.role === "broker" || user?.role === "admin") && (
                <Search
                  placeholder={t("Поиск")}
                  allowClear
                  enterButton
                  size="small"
                  onSearch={onSearch}
                  className={s.search}
                />
              )}
            </div> */}
            {/* <div className={s.user_btn}>
              <Link
                to="/createApplication"
                className={s.btn}
                onClick={() => {
                  dispatch(getCountriesWithPosts());
                  dispatch(getManagerServicesByCountry());
                }}
              >
                {t("Зарегистрировать новую заявку")}
              </Link>
            </div> */}
          </div>
        </div>
      </div>
      <div className={s.content}>
        <h2 className={s.h2}>{t("Все заявления")}</h2>
        <div className={s.form}>
          {width > 700 ? (
            <Table<TableType>
              bordered
              pagination={pagination}
              columns={columns}
              className="table"
              rowClassName={(_, index) =>
                index % 2 === 0 ? "table-row-light" : "table-row-dark"
              }
              onChange={(pagination) => {
                if (pagination?.current) {
                  getNextPage(pagination.current);
                }
              }}
              dataSource={
                Array.isArray(data)
                  ? data?.map((it, index) => ({
                      key: it?.id,
                      number: index + 1,
                      status: (
                        <div className={s.status_bar}>
                          {it.routes?.map((route) => (
                            <div key={route.id} className={s.status_route_item}>
                              <div className={s.status_services_list}>
                                {route.services?.map((srv) => (
                                  <div
                                    key={srv.id}
                                    className={s.status_service_row}
                                  >
                                    <div className={s.status_bar_country}>
                                      <div className={s.aplicationCountry}>
                                        {route.transit_country_name.toUpperCase()}
                                      </div>
                                      <div className={s.status_bar_status}>
                                        {getListStatusTag(srv.status, "sm", t)}
                                      </div>
                                    </div>
                                    <div className={s.status_bar_service}>
                                      <div>{srv.service_name}</div>
                                      <div style={{ fontWeight: "600" }}>
                                        &nbsp;({srv.broker_name})
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ),
                      application_number: it?.application_number,
                      vehicle_number: it?.vehicle_number,
                      broker: it?.broker_details?.company_name
                        ? it?.broker_details?.company_name
                        : it?.broker_details?.full_name,
                      manager: it?.manager_details?.company_name
                        ? it?.manager_details?.company_name
                        : it?.manager_details?.full_name,
                      registration_date: moment(it?.created_at).format(
                        "DD.MM.YYYY - HH:mm",
                      ),
                      // declarant: it?.assigned_declarant?.full_name,
                      // name: it?.user?.full_name,
                      payment:
                        it?.status === "new" ||
                        it?.status === "incomplete" ||
                        it?.status === "corrected" ? (
                          <div> {"-"} </div>
                        ) : (
                          <div
                            onClick={() => {
                              getPaymentAmount(it.id);
                              dispatch(getExchangeRate());
                            }}
                          >
                            <Tooltip title={t("Просмотреть оплату")} key={2}>
                              <CreditCardOutlined style={{ color: "gray" }} />
                            </Tooltip>
                          </div>
                        ),
                      application: (
                        <Link
                          to={`/broker/application/${it?.id}`}
                          className={s.edit}
                          onClick={() => {
                            dispatch(getAdminApplicationDetails(it.id));
                          }}
                        >
                          <Tooltip title={t("Просмотр/Редактирование")} key={1}>
                            <EditOutlined />
                          </Tooltip>
                        </Link>
                      ),
                      history: (
                        <div
                          onClick={() => getMessages(it.id)}
                          className={s.edit}
                        >
                          <Tooltip title={t("Просмотреть историю")} key={2}>
                            <MailOutlined />
                            {/* {it?.unread_message_count_declarant > 0 && (
                                <div className={s.messages_count}>
                                  {it.unread_message_count_declarant}
                                </div>
                              )} */}
                          </Tooltip>
                        </div>
                      ),
                      declaration: (
                        <div className={s.edit}>
                          {/* {it.declaration ? (
                              <Link to={it.declaration} className={s.edit}>
                                <Tooltip title={t("Декларация")} key={4}>
                                  <FileDoneOutlined />
                                </Tooltip>
                              </Link>
                            ) : (
                              "-"
                            )} */}
                        </div>
                      ),
                    }))
                  : []
              }
            />
          ) : (
            <MobileApplicationsList />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminApplicationsList;
